import {
  BriefcaseIcon,
  ClockIcon,
  ExternalLinkIcon,
  IdCardIcon,
  LinkIcon,
  MapPinIcon,
  UserIcon,
} from "lucide-react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  RowSelectionState,
  TableMeta,
  useReactTable,
} from "@tanstack/react-table";

import {
  Column,
  ColumnHeader,
  EmptyState,
  ErrorText,
  LoadingIndicator,
  Text,
  TimeAgoInTableCell,
} from "@/components";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { AtsJobResponse } from "@/types";

interface AtsJobTableMeta extends TableMeta<AtsJobResponse> {
  shouldHideCheckbox: boolean;
}

const columns: ColumnDef<AtsJobResponse>[] = [
  {
    id: "select",
    header: ({ table }) => {
      const meta = table.options.meta as AtsJobTableMeta | undefined;
      if (meta?.shouldHideCheckbox) {
        return null;
      }

      return (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => {
            table.toggleAllPageRowsSelected(!!value);
          }}
        />
      );
    },
    cell: ({ row }) => (
      <Checkbox
        onClick={(e) => {
          e.stopPropagation();
        }}
        checked={row.getIsSelected()}
        onCheckedChange={(value) => {
          row.toggleSelected(!!value);
        }}
      />
    ),
  },
  {
    id: "atsJobId",
    header: () => <ColumnHeader icon={IdCardIcon} label="ATS Job ID" />,
    cell: ({ row }) => <Text>{row.original.atsJobId}</Text>,
  },
  {
    id: "title",
    header: () => <ColumnHeader icon={BriefcaseIcon} label="Title" />,
    cell: ({ row }) => <Text>{row.original.title}</Text>,
  },
  {
    id: "location",
    header: () => <ColumnHeader icon={MapPinIcon} label="Location" />,
    cell: ({ row }) => {
      const city = row.original.city ?? "";
      const state = row.original.state ?? "";
      return <Text>{`${city}, ${state}`}</Text>;
    },
  },
  {
    id: "createdAt",
    header: () => <ColumnHeader icon={ClockIcon} label="Time Orphaned" />,
    cell: ({ row }) => (
      <TimeAgoInTableCell
        className="font-medium"
        timestamp={row.original.createdAt}
      />
    ),
  },
  {
    id: "orphanedCandidatesCount",
    header: () => <ColumnHeader icon={UserIcon} label="Candidates Count" />,
    cell: ({ row }) => <Text>{row.original.orphanedCandidatesCount}</Text>,
  },
  {
    id: "linkToAtsJob",
    header: () => <ColumnHeader icon={LinkIcon} label="ATS Link" />,
    cell: ({ row }) => (
      <a
        aria-label="Open ATS job in new tab"
        href={row.original.url ?? ""}
        rel="noopener noreferrer"
        target="_blank"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <ExternalLinkIcon className="h-5 w-5" />
      </a>
    ),
  },
];

const mapColumnIndexToWidth = ({ index }: { index: number }) => {
  switch (index) {
    case 0:
      return "6%";
    case 1:
      return "14%";
    case 2:
      return "33%";
    case 3:
      return "14%";
    case 4:
      return "12%";
    case 5:
      return "13%";
    case 6:
      return "8%";
  }
};

const TableBodyWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className="overflow-y-auto rounded-b-lg border border-slate-200">
    <Table>
      <TableBody>
        <TableCell colSpan={columns.length}>{children}</TableCell>
      </TableBody>
    </Table>
  </div>
);

export const OrphanedAtsJobTable = ({
  isOrphanedAtsJobsError,
  isOrphanedAtsJobsPending,
  onRowSelectionChange,
  orphanedJobs,
  rowSelection,
}: {
  isOrphanedAtsJobsError: boolean;
  isOrphanedAtsJobsPending: boolean;
  onRowSelectionChange: (selection: RowSelectionState) => void;
  orphanedJobs: AtsJobResponse[];
  rowSelection: RowSelectionState;
}) => {
  const shouldHideCheckbox =
    isOrphanedAtsJobsPending ||
    isOrphanedAtsJobsError ||
    orphanedJobs.length === 0;

  const table = useReactTable({
    data: orphanedJobs,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
    meta: { shouldHideCheckbox },
    onRowSelectionChange: (updater) => {
      const newSelection =
        typeof updater === "function" ? updater(rowSelection) : updater;
      onRowSelectionChange(newSelection);
    },
    state: {
      rowSelection,
    },
  });

  const rows = table.getRowModel().rows;

  const renderTableHeader = () => (
    <div className="rounded-t-lg border border-b-0 border-slate-200">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow className="border-none" key={headerGroup.id}>
              {headerGroup.headers.map((header, i) => (
                <TableHead
                  key={header.id}
                  style={{
                    width: mapColumnIndexToWidth({
                      index: i,
                    }),
                  }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
      </Table>
    </div>
  );

  const renderTableBody = () => {
    if (isOrphanedAtsJobsPending) {
      return (
        <TableBodyWrapper>
          <Column className="items-center justify-center py-14">
            <LoadingIndicator variant="medium" />
          </Column>
        </TableBodyWrapper>
      );
    }
    if (isOrphanedAtsJobsError) {
      return (
        <TableBodyWrapper>
          <Column className="items-center justify-center py-14">
            <ErrorText errorMessage="An error occurred while loading orphaned ATS jobs. Please let the engineers know if they are not already on it!" />
          </Column>
        </TableBodyWrapper>
      );
    }
    if (rows.length === 0) {
      return (
        <TableBodyWrapper>
          <EmptyState
            className="bg-slate-50 py-14"
            description="Any orphaned Workable jobs will appear here."
            headerClassName="text-2xl"
            Icon={BriefcaseIcon}
            title="No orphaned Workable jobs"
          />
        </TableBodyWrapper>
      );
    }
    return (
      <div className="overflow-y-auto rounded-b-lg border border-slate-200">
        <Table>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                className="cursor-pointer"
                key={row.id}
                data-state={row.getIsSelected() && "selected"}
                onClick={row.getToggleSelectedHandler()}
              >
                {row.getVisibleCells().map((cell, i) => (
                  <TableCell
                    key={cell.id}
                    style={{
                      width: mapColumnIndexToWidth({
                        index: i,
                      }),
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <Column className="overflow-auto">
      {renderTableHeader()}
      {renderTableBody()}
    </Column>
  );
};
